import { ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import { ClusterProjectRow } from "in_queue/types/clusterProjectRowType";
import { RankCell } from "../RankPill";
import {
    compareNullsLast,
    currencyFormatter,
    NiraColGroupDef,
    roundingFormatter
} from "./agGridHelpers";

/** The `cluster_projects` fields the Base Scenario columns read. */
export type BaseScenarioStats = Pick<
    ClusterProjectRow,
    "costPerMw" | "sumAllocatedCost" | "numAssignedUpgrades" | "rankStats"
>;

export type BaseScenarioColumnId =
    | "costPerMw"
    | "totalCost"
    | "upgrades"
    | "rank"
    | "percentile";

/**
 * The "Base Scenario" column group: $/MW, total cost, upgrade count and rank,
 * as the cluster's latest base scenario reported them.
 *
 * `getStats` says how to reach those fields from a row, so a table whose row
 * *is* a `ClusterProjectRow` and one that merely joins to one share the same
 * columns. Every column dashes out for a project the base scenario did not
 * rank, and sorts those rows last in both directions.
 */
export const baseScenarioColumnGroup = <TData,>({
    getStats,
    headerTooltip,
    includePercentile = false
}: {
    readonly getStats: (
        data: TData | undefined
    ) => BaseScenarioStats | undefined;
    readonly headerTooltip: string;
    /** A hidden column carrying percentile into the CSV. */
    readonly includePercentile?: boolean;
}): NiraColGroupDef<TData, BaseScenarioColumnId> => ({
    headerName: "Base Scenario",
    headerTooltip,
    children: [
        {
            colId: "costPerMw",
            headerName: "$/MW",
            headerTooltip,
            type: "numericColumn",
            flex: 0.8,
            minWidth: 84,
            valueGetter: ({ data }) => getStats(data)?.costPerMw ?? null,
            valueFormatter: currencyFormatter,
            comparator: compareNullsLast
        },
        {
            colId: "totalCost",
            headerName: "Total Cost",
            headerTooltip:
                "Total upgrade cost allocated to this project in the latest base scenario.",
            type: "numericColumn",
            flex: 0.9,
            minWidth: 123,
            valueGetter: ({ data }) => getStats(data)?.sumAllocatedCost ?? null,
            valueFormatter: currencyFormatter,
            comparator: compareNullsLast
        },
        {
            colId: "upgrades",
            headerName: "Upgrades",
            headerTooltip:
                "Distinct network upgrades with cost allocated to this project in the latest base scenario. 0 means it ran the project and allocated nothing.",
            type: "numericColumn",
            flex: 0.8,
            minWidth: 112,
            valueGetter: ({ data }) =>
                getStats(data)?.numAssignedUpgrades ?? null,
            valueFormatter: roundingFormatter,
            comparator: compareNullsLast
        },
        {
            colId: "rank",
            headerName: "Rank",
            headerTooltip:
                "Project rank by $/MW across the cluster in the latest base scenario (1 = cheapest).",
            type: "numericColumn",
            flex: 0.6,
            minWidth: 74,
            valueGetter: ({ data }) => getStats(data)?.rankStats?.rank ?? null,
            valueFormatter: ({ data }: ValueFormatterParams<TData>) => {
                const stats = getStats(data)?.rankStats;
                return stats
                    ? `${stats.rank} out of ${stats.totalRanked}`
                    : "-";
            },
            useValueFormatterForExport: true,
            comparator: compareNullsLast,
            cellRenderer: ({ data }: ICellRendererParams<TData>) => {
                const stats = getStats(data)?.rankStats;
                return (
                    <RankCell
                        rank={stats?.rank}
                        totalRanked={stats?.totalRanked}
                        percentile={stats?.percentile}
                    />
                );
            }
        },
        ...(includePercentile
            ? [
                  {
                      colId: "percentile" as const,
                      headerName: "Percentile",
                      type: "numericColumn",
                      // The rank pill already shows this, so it earns no space
                      // in the grid. It is here to reach the export as a plain
                      // number, which the pill's own column cannot carry.
                      hide: true,
                      valueGetter: ({ data }: { data: TData | undefined }) =>
                          getStats(data)?.rankStats?.percentile ?? null,
                      comparator: compareNullsLast
                  }
              ]
            : [])
    ]
});
